import React, { useState, useEffect } from "react";
import Pica from "pica";

// Helper function to generate a random string of given length
function generateRandomHash(length = 6) {
  const chars = "abcdefghijklmnopqrstuvwxyz1234567890";
  let hash = "";
  for (let i = 0; i < length; i++) {
    hash += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return hash;
}

// If filename is longer than 15 characters, return a random 6-letter hash.
function hashFilename(filename, threshold = 60) {
  if (filename.length <= threshold) return filename;
  return generateRandomHash(6);
}

function FileList({ files, onUpdateFiles }) {
  const [processedFiles, setProcessedFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const handlePreview = async (fileObj) => {
    // Updated defaults: 40 for xOffset, 38 for yOffset.
    const processedBlob = await processImage(
      fileObj.file,
      fileObj.xOffset || 40,
      fileObj.yOffset || 38
    );
    setPreviewFile({ ...fileObj, processedBlob });
  };

  const closePreview = () => {
    setPreviewFile(null);
  };

  const autoCropTransparent = async (image) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let minX = width, minY = height, maxX = 0, maxY = 0;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }
      }
    }
    const croppedWidth = maxX - minX + 1;
    const croppedHeight = maxY - minY + 1;
    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = croppedWidth;
    croppedCanvas.height = croppedHeight;
    const croppedCtx = croppedCanvas.getContext("2d");
    croppedCtx.drawImage(
      canvas,
      minX,
      minY,
      croppedWidth,
      croppedHeight,
      0,
      0,
      croppedWidth,
      croppedHeight
    );
    return croppedCanvas;
  };

  const overlayOnTemplate = async (croppedCanvas, xOffset = 8, yOffset = 42) => {
    const templateCanvas = document.createElement("canvas");
    // Updated dimensions: width 256; height 512.
    templateCanvas.width = 256;
    templateCanvas.height = 512;
    const ctx = templateCanvas.getContext("2d");
    ctx.fillStyle = "rgba(0, 0, 0, 0)";
    ctx.fillRect(0, 0, 256, 512);
    // Draw the cropped image at the given offsets (no scaling)
    ctx.drawImage(croppedCanvas, xOffset, yOffset);
    return templateCanvas;
  };

  const processImage = async (file, xOffset = 40, yOffset = 38) => {
    const pica = Pica();
    const img = new Image();
    img.src = URL.createObjectURL(file);
    return new Promise((resolve) => {
      img.onload = async () => {
        const croppedCanvas = await autoCropTransparent(img);
        const finalCanvas = await overlayOnTemplate(croppedCanvas, xOffset, yOffset);
        const finalBlob = await pica.toBlob(finalCanvas, "image/png");
        resolve(finalBlob);
      };
    });
  };

  const handleDelete = (index) => {
    const updatedFiles = processedFiles.filter((_, i) => i !== index);
    setProcessedFiles(updatedFiles);
    onUpdateFiles(updatedFiles);
  };

  const handleOffsetChange = async (index, offsetType, value) => {
    const updatedFiles = [...processedFiles];
    const fileObj = updatedFiles[index];
    updatedFiles[index] = {
      ...fileObj,
      [offsetType]: value !== "" ? parseFloat(value) : value,
    };
    setProcessedFiles(updatedFiles);
    const processedBlob = await processImage(
      fileObj.file,
      offsetType === "xOffset" ? parseFloat(value) : fileObj.xOffset || 40,
      offsetType === "yOffset" ? parseFloat(value) : fileObj.yOffset || 38
    );
    updatedFiles[index].processedBlob = processedBlob;
    setProcessedFiles(updatedFiles);
    onUpdateFiles(updatedFiles);
  };

  const handleCharChange = (index, value) => {
    const updatedFiles = [...processedFiles];
    updatedFiles[index].char = value;
    setProcessedFiles(updatedFiles);
    onUpdateFiles(updatedFiles);
  };

  const handleBackgroundChange = (index, value) => {
    const updatedFiles = [...processedFiles];
    // Save the boolean value (true/false) explicitly.
    updatedFiles[index] = { ...updatedFiles[index], includeBackground: value };
    setProcessedFiles(updatedFiles);
    onUpdateFiles(updatedFiles);
  };

  useEffect(() => {
    const processAllFiles = async () => {
      setProcessing(true);
      const unprocessedFiles = files.filter(
        (fileObj) => !processedFiles.some((p) => p.file === fileObj.file)
      );
      if (unprocessedFiles.length === 0) {
        setProcessing(false);
        return;
      }
      const updatedFiles = await Promise.all(
        unprocessedFiles.map(async (fileObj) => {
          const processedBlob = await processImage(
            fileObj.file,
            fileObj.xOffset || 40,
            fileObj.yOffset || 38
          );
          const originalFileName =
            fileObj.file && fileObj.file.name ? fileObj.file.name : fileObj.name;
          const hashedName = hashFilename(originalFileName);
          // Default includeBackground to true if not already set.
          return {
            ...fileObj,
            processedBlob,
            xOffset: fileObj.xOffset || 40,
            yOffset: fileObj.yOffset || 38,
            includeBackground:
              fileObj.includeBackground !== undefined ? fileObj.includeBackground : true,
            originalFileName,
            hashedName,
          };
        })
      );
      setProcessedFiles((prevFiles) => [...prevFiles, ...updatedFiles]);
      setProcessing(false);
    };
    processAllFiles();
  }, [files]);

  useEffect(() => {
    if (previewFile) {
      const canvas = document.getElementById("previewCanvas");
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const loadProcessedImage = () => {
        if (previewFile.processedBlob) {
          const uploadedImage = new Image();
          const processedBlobURL = URL.createObjectURL(previewFile.processedBlob);
          uploadedImage.src = processedBlobURL;
          uploadedImage.onload = () => {
            ctx.drawImage(uploadedImage, 0, 0);
            URL.revokeObjectURL(processedBlobURL);
          };
          uploadedImage.onerror = () =>
            console.error("Failed to load the processed image.");
        }
      };

      if (previewFile.includeBackground) {
        const background = new Image();
        background.src = process.env.PUBLIC_URL + "/preview-image.png";
        background.onload = () => {
          const bgOffsetX = (canvas.width - background.width) / 2;
          ctx.drawImage(background, bgOffsetX, 0);
          loadProcessedImage();
        };
        background.onerror = () => {
          console.error("Failed to load background image.");
          loadProcessedImage();
        };
      } else {
        loadProcessedImage();
      }
    }
  }, [previewFile]);

  return (
    <div className="file-list">
      <p className="notice">
        Note: The default offsets (X: 40, Y: 38) should work for most cases, but if
        your image has parts extending beyond the GUI, you might need to adjust
        these values.
      </p>
      {processedFiles.map((fileObj, index) => (
        <div key={index} className="file-item">
          <img
            src={URL.createObjectURL(fileObj.file)}
            alt={fileObj.originalFileName}
            className="file-preview"
          />
          <div className="file-details">
            <p className="file-name">
              <strong>Original:</strong> {fileObj.originalFileName} <br />
              <strong>Hashed:</strong> {fileObj.hashedName}
            </p>
            <input
              type="text"
              placeholder="Unicode Character"
              maxLength={1}
              value={fileObj.char}
              onChange={(e) => {
                const updatedFiles = [...processedFiles];
                updatedFiles[index].char = e.target.value;
                setProcessedFiles(updatedFiles);
                onUpdateFiles(updatedFiles);
              }}
              className="file-input"
            />
            <label className="offset-label">
              X Offset:
              <input
                type="number"
                step="0.1"
                placeholder="X Offset"
                value={fileObj.xOffset || ""}
                onChange={(e) =>
                  handleOffsetChange(index, "xOffset", e.target.value)
                }
                className="offset-input"
              />
            </label>
            <label className="offset-label">
              Y Offset:
              <input
                type="number"
                step="0.1"
                placeholder="Y Offset"
                value={fileObj.yOffset || ""}
                onChange={(e) =>
                  handleOffsetChange(index, "yOffset", e.target.value)
                }
                className="offset-input"
              />
            </label>
            <label className="toggle-background">
              <input
                type="checkbox"
                checked={fileObj.includeBackground !== false}
                onChange={(e) => handleBackgroundChange(index, e.target.checked)}
              />{" "}
              Include Background
            </label>
            <button onClick={() => handlePreview(fileObj)} className="preview-button">
              Preview
            </button>
            <button onClick={() => handleDelete(index)} className="delete-button">
              Delete
            </button>
          </div>
        </div>
      ))}
      {previewFile && (
        <div className="preview-modal">
          <div className="preview-content">
            <button onClick={closePreview} className="close-preview">
              Close
            </button>
            <canvas id="previewCanvas" width={256} height={512}></canvas>
            <p style={{ color: "#ffffff", textAlign: "center", marginTop: "10px" }}>
              Actual dimensions: 256x512.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default FileList;
